import { CircularProgress, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import {
  campaignsApi,
  useChangeEngagementScoreConfigMutation,
  useGetEngagementScoreConfigQuery,
  accountsApi,
} from "../../../../api";
import { setRecalculatingStatus } from "../../../../features/companySlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { Analytics, EngagementScoreConfig } from "../../../../types/apiTypes";
import { SettingsIcon } from "../../../images";
import { EngagementScoreSettingModal } from "../../../ui/modal";
import { EngagementRate } from "../EngagementRate";
import { AnalyticCardWrapper } from "../AnalyticCardWrapper";
import { CustomToast } from "../../../ui/CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { toast } from "react-toastify";

export const AccountsAverageRateBlock = ({
  analytics,
  campaign_id,
}: {
  analytics: Analytics;
  campaign_id: string;
}) => {
  const dispatch = useAppDispatch();
  const { isEditor } = useAppSelector((state) => state.company.userRoles);
  const recalculation_status = useAppSelector(
    (state) => state.company.recalculation_status,
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isRecalculatingScores, setIsRecalculatingScores] = useState<boolean>(
    analytics.is_recalculating_scores,
  );

  const { data, isLoading } = useGetEngagementScoreConfigQuery({
    campaign_id,
  });

  const [changeEngagementScore] = useChangeEngagementScoreConfigMutation();

  useEffect(() => {
    if (recalculation_status === "stop") {
      setIsRecalculatingScores(false);
      dispatch(accountsApi.util.invalidateTags(["list-view", "analytics"]));
      dispatch(campaignsApi.util.invalidateTags(["engagement-config"]));
      dispatch(setRecalculatingStatus(""));
    }
  }, [recalculation_status]);

  const onSubmit = (value: EngagementScoreConfig) => {
    setIsRecalculatingScores(true);
    changeEngagementScore({
      campaign_id,
      ...value,
    })
      .unwrap()
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Change Settings Failed"}
          />,
        );
      })
      .finally(() => {
        setIsOpen(false);
      });
  };

  const openSettingsHandler = () => {
    if (isLoading) return;
    setIsOpen(true);
  };

  return (
    <AnalyticCardWrapper
      title="Engagement Rate"
      tooltip="Average engagement rate of all identified BC members within analyzed accounts.
       Engagement rate refers to how active and efficient the communication with identified BC
        members is, based on phone calls, emails, meetings, and communication recency data."
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        flexGrow={1}
        position="relative"
      >
        {isEditor && (
          <button
            onClick={openSettingsHandler}
            style={{
              position: "absolute",
              bottom: 0,
              right: -2,
              padding: "4px",
              border: isOpen ? "1px solid #1C3C6C" : "none",
              borderRadius: "8px",
              cursor: isRecalculatingScores ? "auto" : "pointer",
              background: "none",
            }}
            disabled={isRecalculatingScores}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: "#1C3C6C" }} />
            ) : (
              <SettingsIcon opacity={isRecalculatingScores ? "0.5" : "1"} />
            )}
          </button>
        )}

        {isRecalculatingScores ? (
          <Stack
            sx={{ position: "relative", width: "112px", height: "112px" }}
            justifyContent="center"
          >
            <CircularProgress
              size={112}
              thickness={6}
              sx={{ color: "#1C3C6C" }}
            />
          </Stack>
        ) : (
          <EngagementRate
            value={analytics.average_engagement_score || 0}
            indicator={analytics.average_engagement_score_indicator}
            indicatorChange={analytics.average_engagement_change}
            indicatorChangeAt={analytics.average_engagement_changed_at}
          />
        )}

        {isOpen && data && (
          <EngagementScoreSettingModal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            isLoading={isRecalculatingScores}
            settings={data}
            onSubmit={onSubmit}
          />
        )}
      </Stack>
    </AnalyticCardWrapper>
  );
};
